import { useEffect, type ReactNode, type RefObject } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";

import { cx } from "@/lib/cx";
import { playSfx } from "@/platform/sfx";
import { OverflowMenu, type OverflowItem } from "./Menu";
import { OverlayHeader } from "./OverlayHeader";
import { ScrollShadow } from "./ScrollShadow";
import { LAYER, sheetSurface } from "./surface";
import { useBackClose } from "./useBackClose";

/**
 * The one modal shell.
 *
 * There were three (UI-REVIEW P11 and §5): three hand-rolled `<dialog>`
 * elements with three paddings, three header treatments and three footer
 * layouts, so "a question" looked like a different kind of event depending on
 * which screen asked it. This replaces all three.
 *
 * **Reserve it for a genuine interruption.** Editing is not one: task and card
 * detail belong in the {@link Peek}, which does not block the list behind it.
 * What is left is destructive-and-irreversible confirmation, creating a new
 * project, and the image lightbox — three things, all of which are correctly
 * modal.
 *
 * Two rules the old shells broke, both encoded here rather than left to the
 * caller:
 *
 * 1. **The body scrolls, and says so.** {@link ScrollShadow} draws a gradient
 *    at whichever edge has content beyond it. The card editor used to slice
 *    "Due date" through the middle under a sticky footer with no shadow.
 * 2. **One footer grammar.** Destructive far-left, inside a `⋯` overflow menu;
 *    cancel then primary far-right. Delete used to sit bottom-left as a plain
 *    ghost button — the most destructive control rendered as the quietest.
 */

export type DialogSize = "sm" | "md" | "lg";

const SIZES: Record<DialogSize, string> = {
  sm: "sm:max-w-[26rem]",
  md: "sm:max-w-[34rem]",
  lg: "sm:max-w-[46rem]",
};

export type DialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  /** Sits under the title and is also the dialog's accessible description. */
  description?: ReactNode;
  size?: DialogSize;
  /** Use {@link DialogFooter} unless there is a reason not to. */
  footer?: ReactNode;
  /**
   * Escape and outside-click close by default. Turn this off while a write is
   * in flight, or while a form is dirty and the caller wants to ask first.
   */
  dismissible?: boolean;
  /**
   * Where focus lands on open, instead of whatever comes first in the DOM.
   *
   * May point at the element to focus, or at a container — the first focusable
   * thing inside it is used. Prefer the element: `Button` takes a `ref`, so
   * "focus the Cancel button" is a ref on the button. The container form is
   * kept for composites where the caller does not own the individual control.
   */
  initialFocus?: RefObject<HTMLElement | null>;
  /** Hides the header ✕. The footer still needs a Cancel. */
  hideClose?: boolean;
  /**
   * Whether the panel and scrim animate as they mount and unmount.
   * Disable this for utility surfaces that should appear and disappear
   * immediately.
   */
  animated?: boolean;
  children: ReactNode;
  className?: string;
  /** Extra classes on the scrolling body — padding lives here. */
  bodyClassName?: string;
};

export function Dialog({
  open,
  onOpenChange,
  title,
  description,
  size = "md",
  footer,
  dismissible = true,
  initialFocus,
  hideClose = false,
  animated = true,
  children,
  className,
  bodyClassName,
}: DialogProps) {
  useEffect(() => {
    if (open) {
      playSfx("open");
    }
  }, [open]);

  // The phone's Back is one more way to dismiss, so it obeys `dismissible`.
  useBackClose(open, () => {
    if (dismissible) {
      onOpenChange(false);
    }
  });

  const block = (event: Event) => {
    if (!dismissible) {
      event.preventDefault();
    }
  };

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        {/* A scrim, not a blur. The opaque overlay panel already separates the
            modal from the page; blurring the backdrop would add a compositor
            layer without revealing anything useful. */}
        <DialogPrimitive.Overlay
          data-busee-anim={animated ? "fade" : undefined}
          className={cx("fixed inset-0 bg-black/40", LAYER.overlay)}
        />

        <DialogPrimitive.Content
          data-busee-anim={animated ? "dialog" : undefined}
          onEscapeKeyDown={block}
          onPointerDownOutside={block}
          onInteractOutside={block}
          onOpenAutoFocus={(event) => {
            const target = focusTarget(initialFocus?.current ?? null);
            if (target) {
              event.preventDefault();
              target.focus();
            }
          }}
          className={cx(
            "fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2",
            // A phone gets the full width minus a gutter; a desktop gets the
            // size step. `max-h` keeps the shell inside the viewport so the
            // body — not the page — is what scrolls.
            "w-[calc(100vw-2rem)] max-h-[min(42rem,calc(100dvh-2rem))]",
            SIZES[size],
            sheetSurface,
            "rounded-surface shadow-pop",
            LAYER.overlay,
            className,
          )}
        >
          <OverlayHeader
            title={title}
            titleLabel={typeof title === "string" ? title : undefined}
            onClose={hideClose ? undefined : () => onOpenChange(false)}
            wrapTitle={(heading) => <DialogPrimitive.Title asChild>{heading}</DialogPrimitive.Title>}
            wrapClose={(button) => <DialogPrimitive.Close asChild>{button}</DialogPrimitive.Close>}
          />

          <ScrollShadow contentClassName={cx("p-5", bodyClassName)}>
            {description ? (
              <DialogPrimitive.Description className="mb-2 text-caption text-muted">
                {description}
              </DialogPrimitive.Description>
            ) : (
              // Radix warns when a dialog has no description; saying "there
              // is none" explicitly is the documented way to mean it.
              <DialogPrimitive.Description />
            )}
            {children}
          </ScrollShadow>

          {footer ? (
            // The only edge in the shell, and it earns it: this is exactly the
            // "two scroll regions meet" case the elevation rule allows. It is a
            // hairline rather than a 1px border, so the separator stays quiet
            // against the overlay panel in both themes.
            // `min-h-10`, not `h-10`. {@link DialogFooter} wraps, and on a
            // 288px-wide dialog — a 320px phone minus its gutter — an overflow
            // menu, a note, Cancel and a primary do wrap. A fixed height would
            // hide whichever row lost, and the row that loses is the one with
            // the primary button in it.
            <div className="flex min-h-16 shrink-0 items-center hairline-t px-5 py-3">
              {footer}
            </div>
          ) : null}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

const FOCUSABLE =
  "button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex='-1'])";

/** The element itself when it can hold focus, otherwise the first inside it. */
function focusTarget(node: HTMLElement | null): HTMLElement | null {
  if (!node) {
    return null;
  }
  return node.matches(FOCUSABLE) ? node : node.querySelector<HTMLElement>(FOCUSABLE);
}

/* -------------------------------------------------------------------------- */

export type DialogFooterProps = {
  /**
   * Destructive and rarely-wanted actions. They are collected into a `⋯` menu
   * on the far left rather than sitting in the tab order next to Save — one
   * misplaced Return should not be able to delete anything.
   */
  overflow?: OverflowItem[];
  /** Usually a `<Button variant="secondary">Cancel</Button>`. */
  cancel?: ReactNode;
  /** The single affirmative action. */
  primary?: ReactNode;
  /** Anything else on the left — a validation note, a keyboard hint. */
  children?: ReactNode;
  className?: string;
};

export function DialogFooter({
  overflow,
  cancel,
  primary,
  children,
  className,
}: DialogFooterProps) {
  return (
    <div className={cx("flex w-full flex-wrap items-center gap-3", className)}>
      {overflow && overflow.length > 0 ? <OverflowMenu items={overflow} side="top" /> : null}
      {children ? <div className="min-w-0 text-caption text-muted">{children}</div> : null}
      <div className="ml-auto flex items-center gap-2">
        {cancel}
        {primary}
      </div>
    </div>
  );
}

/** Re-exported so a caller can close from inside the body without a callback. */
export const DialogClose = DialogPrimitive.Close;
